import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { IntentSelector } from './components/IntentSelector';
import { ChatPanel } from './components/ChatPanel';
import { ScenarioSummary, type Scenario } from './components/ScenarioSummary';
import { PersonalReport } from './components/PersonalReport';
import { FileText, ListChecks } from 'lucide-react';
type Intent = 'purchase' | 'refinance' | 'equity';
type SidebarTab = 'scenario' | 'report';
export function App() {
  const [intent, setIntent] = useState<Intent | null>(null);
  const [scenario, setScenario] = useState<Scenario>({
    intent: 'purchase'
  });
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('scenario');
  const handleSelectIntent = (selected: Intent) => {
    setScenario({
      intent: selected
    });
    setIntent(selected);
  };
  const handleReset = () => {
    setIntent(null);
    setScenario({
      intent: 'purchase'
    });
  };
  const handleUpdate = (patch: Partial<Scenario>) => {
    setScenario(prev => ({
      ...prev,
      ...patch
    }));
  };
  const {
    progress,
    estimatedPayment,
    currentPayment,
    monthlySavings,
    accessibleEquity
  } = useMemo(() => {
    if (!intent) {
      return {
        progress: 0,
        estimatedPayment: undefined,
        currentPayment: undefined,
        monthlySavings: undefined,
        accessibleEquity: undefined
      };
    }
    const fieldsByIntent: Record<Intent, (keyof Scenario)[]> = {
      purchase: ['targetRate', 'budget', 'propertyState', 'downPayment', 'loanTerm', 'underContract', 'timeline', 'agent', 'name', 'email'],
      refinance: ['currentRate', 'targetRate', 'goal', 'propertyState', 'homeValue', 'currentBalance', 'loanTerm', 'name', 'email'],
      equity: ['homeValue', 'propertyState', 'currentBalance', 'cashOutAmount', 'goal', 'timeline', 'name', 'email']
    };
    const fields = fieldsByIntent[intent];
    const filled = fields.filter(f => scenario[f] != null && scenario[f] !== '').length;
    const prog = filled / fields.length;
    // P&I helper for purchase + refinance.
    const monthlyPI = (principal: number, ratePct: number, years: number) => {
      const r = ratePct / 100 / 12;
      const n = years * 12;
      if (principal <= 0 || r <= 0 || n <= 0) return 0;
      return Math.round(principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
    };
    let payment: number | undefined;
    let currentPayment: number | undefined;
    let monthlySavings: number | undefined;
    let accessibleEquity: number | undefined;
    const targetRate = scenario.targetRate;
    const currentRate = scenario.currentRate;
    const years = scenario.loanTerm ?? 30;
    if (intent === 'purchase' && scenario.budget && scenario.downPayment != null && targetRate && targetRate > 0) {
      const principal = Math.max(0, scenario.budget - scenario.downPayment);
      payment = monthlyPI(principal, targetRate, years);
    } else if (intent === 'refinance' && scenario.currentBalance) {
      if (targetRate && targetRate > 0) {
        payment = monthlyPI(scenario.currentBalance, targetRate, years);
      }
      if (currentRate && currentRate > 0) {
        currentPayment = monthlyPI(scenario.currentBalance, currentRate, years);
        if (payment != null && currentPayment > 0) {
          monthlySavings = currentPayment - payment;
        }
      }
    } else if (intent === 'equity') {
      // HELOC: no rate questions. Headline = how much equity is accessible.
      //   Accessible Home Equity = homeValue * 0.9 - currentBalance
      //   Approx equity payment  = cashOutAmount * 0.07 / 12   (interest-only @ 7%)
      if (scenario.homeValue && scenario.currentBalance != null) {
        accessibleEquity = Math.max(0, Math.round(scenario.homeValue * 0.9 - scenario.currentBalance));
      }
      if (scenario.cashOutAmount && scenario.cashOutAmount > 0) {
        payment = Math.round(scenario.cashOutAmount * 0.07 / 12);
      }
    }
    return {
      progress: prog,
      estimatedPayment: payment,
      currentPayment,
      monthlySavings,
      accessibleEquity
    };
  }, [intent, scenario]);
  const intentMeta: Record<Intent, {
    emoji: string;
    label: string;
  }> = {
    purchase: {
      emoji: '🔑',
      label: 'Purchase'
    },
    refinance: {
      emoji: '🔄',
      label: 'Refinance'
    },
    equity: {
      emoji: '🏡',
      label: 'Home Equity'
    }
  };
  return <div className="min-h-screen bg-cream font-sans selection:bg-lime-200 selection:text-forest" data-id="element-0">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-id="element-1">
        <AnimatePresence mode="wait" data-id="element-2">
          {!intent ? <motion.div key="intent" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.3
        }} data-id="element-3">
              <IntentSelector onSelect={handleSelectIntent} data-id="element-4" />
            </motion.div> : <motion.div key="chat" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-5">
              <div className="flex items-center justify-between mb-8" data-id="element-6">
                <button onClick={handleReset} className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest/60 hover:text-forest transition-colors" data-id="element-7">
                  <ArrowLeft className="w-4 h-4" data-id="element-8" />
                  Start over
                </button>
                <div className="text-sm font-semibold text-forest/70" data-id="element-9">
                  {intentMeta[intent].emoji} {intentMeta[intent].label}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start" data-id="element-10">
                <div className="lg:col-span-8 order-2 lg:order-1" data-id="element-11">
                  <ChatPanel intent={intent} scenario={scenario} onUpdate={handleUpdate} data-id="element-12" />
                </div>
                <div className="lg:col-span-4 order-1 lg:order-2 lg:sticky lg:top-24" data-id="element-13">
                  {/* Sidebar tabs: Scenario vs Personalized Report */}
                  <div className="flex items-center gap-1 p-1 mb-3 bg-white rounded-full border border-forest/10 shadow-sm" data-id="element-14">
                    <SidebarTabButton active={sidebarTab === 'scenario'} onClick={() => setSidebarTab('scenario')} icon={<ListChecks className="w-4 h-4" data-id="element-16" />} label="Scenario" data-id="element-15" />
                    <SidebarTabButton active={sidebarTab === 'report'} onClick={() => setSidebarTab('report')} icon={<FileText className="w-4 h-4" data-id="element-18" />} label="Report" data-id="element-17" />
                  </div>

                  <AnimatePresence mode="wait" data-id="element-19">
                    {sidebarTab === 'scenario' ? <motion.div key="sb-scenario" initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -6
                }} transition={{
                  duration: 0.18
                }} data-id="element-20">
                        <ScenarioSummary scenario={scenario} estimatedPayment={estimatedPayment} currentPayment={currentPayment} monthlySavings={monthlySavings} accessibleEquity={accessibleEquity} progress={progress} data-id="element-21" />
                      </motion.div> : <motion.div key="sb-report" initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -6
                }} transition={{
                  duration: 0.18
                }} data-id="element-22">
                        <PersonalReport scenario={scenario} estimatedPayment={estimatedPayment} currentPayment={currentPayment} monthlySavings={monthlySavings} accessibleEquity={accessibleEquity} data-id="element-23" />
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </main>
    </div>;
}
interface SidebarTabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}
function SidebarTabButton({
  active,
  onClick,
  icon,
  label
}: SidebarTabButtonProps) {
  return <button onClick={onClick} className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-sm font-bold transition-all ${active ? 'bg-forest text-white shadow-sm' : 'text-forest/60 hover:text-forest hover:bg-cream/60'}`} data-id="element-24">
      {icon}
      {label}
    </button>;
}